import {
  Button,
  DatePicker,
  Drawer,
  FormField,
  Input,
  NumberInput,
  SegmentedControl,
} from '@ntconsult/aluma-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ALLOCATION_COLUMN_LABEL_KEYS, type AllocationColumn } from '../hooks/useAllocationColumns';
import type { AdvancedFilterValues, AllocationFilterState } from '../hooks/useAllocationFilters';
import { useClients } from '../hooks/useClients';
import { useContracts } from '../hooks/useContracts';
import { useMultiAllocatedProfessionals, useProfessionals } from '../hooks/useProfessionals';
import { useProjects } from '../hooks/useProjects';
import { useTagFilterOptions } from '../hooks/useTagFilterOptions';
import type { ProfessionalOption } from '../types/Professional';
import { contractLabel, professionalLabel } from '../utils/labels';
import { MultiSearchCombobox } from './MultiSearchCombobox';
import { MultiSelectFilter } from './MultiSelectFilter';
import { SearchCombobox } from './SearchCombobox';

const TITLE_ID = 'allocations-advanced-filters-title';

type BillableChoice = 'all' | 'yes' | 'no';

type ManagerSearchKey = 'accountDirectorSearch' | 'operationManagerSearch' | 'projectManagerSearch';

const MANAGER_FIELDS = [
  ['accountDirector', 'accountDirectorSearch'],
  ['operationsManager', 'operationManagerSearch'],
  ['projectManager', 'projectManagerSearch'],
] as const satisfies readonly (readonly [AllocationColumn, ManagerSearchKey])[];

type AdvancedFiltersDrawerProps = {
  open: boolean;
  onClose: () => void;
  filterState: AllocationFilterState;
  /** Colunas visíveis da tabela ("Colunas"): cada uma traz o seu campo de filtro. */
  isColumnVisible: (column: AllocationColumn) => boolean;
  multiAllocated?: boolean;
};

function draftFrom(filterState: AllocationFilterState): AdvancedFilterValues {
  const { filters } = filterState;
  return {
    professionals: filterState.professionals,
    project: filterState.project,
    contract: filterState.contract,
    client: filterState.client,
    percentage: { min: filters.minPercentage ?? null, max: filters.maxPercentage ?? null },
    billable: filters.billable ?? null,
    notes: filters.notes ?? '',
    tags: filterState.tags,
    accountDirectorSearch: filters.accountDirectorSearch ?? '',
    operationManagerSearch: filters.operationManagerSearch ?? '',
    projectManagerSearch: filters.projectManagerSearch ?? '',
    startDate: filterState.startDate,
    endDate: filterState.endDate,
  };
}

const toProfessionalOption = (professional: ProfessionalOption) => ({
  id: professional.id,
  name: professional.name.trim(),
});

const toChoice = (billable: boolean | null): BillableChoice =>
  billable === null ? 'all' : billable ? 'yes' : 'no';

const fromChoice = (choice: BillableChoice): boolean | null =>
  choice === 'all' ? null : choice === 'yes';

/**
 * Os campos acompanham as colunas visíveis (T24): marcar uma coluna em
 * "Colunas" traz o filtro dela, desmarcar tira. Profissional é sempre visível.
 * Colunas sem filtro na API (unidade, rateio, perfil de venda e valores) não
 * têm campo. Filtro de coluna escondida continua aplicado e no chip.
 *
 * Projeto/Contrato/Cliente são seleção exata (combobox que já abre com a
 * lista) e convivem com a busca "contém" dos cabeçalhos; Contrato lista só os
 * do Cliente escolhido, quando há um. Início/Fim filtram por SOBREPOSIÇÃO com
 * o período da própria alocação — Fim vazio = do Início em diante, Início
 * vazio = até o Fim. Rascunho local, comitado de uma vez no "Aplicar".
 */
export function AdvancedFiltersDrawer({
  open,
  onClose,
  filterState,
  isColumnVisible,
  multiAllocated = false,
}: AdvancedFiltersDrawerProps) {
  const { t } = useTranslation(['alocacao', 'common']);
  const [draft, setDraft] = useState(() => draftFrom(filterState));
  const [wasOpen, setWasOpen] = useState(open);
  const tagOptions = useTagFilterOptions(draft.tags);

  // Reinicia o rascunho a partir do que está aplicado sempre que o painel abre.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setDraft(draftFrom(filterState));
  }

  const clientId = draft.client?.id;
  // Hook repassado ao `SearchCombobox`, que o chama no topo do próprio render
  // — sempre exatamente um `useQuery`, só o `clientId` varia.
  const useContractsOfClient = useCallback(
    // biome-ignore lint/correctness/useHookAtTopLevel: ver comentário acima
    (search: string) => useContracts(search, { clientId }),
    [clientId],
  );

  const apply = () => {
    filterState.applyAdvanced(draft);
    onClose();
  };

  return (
    <Drawer open={open} onClose={onClose} size="md" ariaLabelledBy={TITLE_ID}>
      <Drawer.Header
        titleId={TITLE_ID}
        dismissLabel={t('common:close')}
        title={t('filtros.avancados')}
        subtitle={t('filtros.avancadosDescricao')}
        onClose={onClose}
      />
      <Drawer.Body>
        <form
          id="allocations-advanced-filters"
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            apply();
          }}
        >
          <FormField label={t('filtros.profissional')}>
            <MultiSearchCombobox
              label={t('filtros.profissional')}
              placeholder={t('filtros.profissionaisPlaceholder')}
              searchPlaceholder={t('filtros.buscarPlaceholder')}
              emptyLabel={t('filtros.semResultados')}
              removeChipLabel={(name) => t('filtros.chips.remover', { filter: name })}
              value={draft.professionals}
              onChange={(professionals) => setDraft((d) => ({ ...d, professionals }))}
              useSearch={multiAllocated ? useMultiAllocatedProfessionals : useProfessionals}
              toOption={toProfessionalOption}
              getItemLabel={professionalLabel}
            />
          </FormField>

          {isColumnVisible('project') && (
            <FormField label={t('filtros.projeto')}>
              <SearchCombobox
                placeholder={t('filtros.buscarPlaceholder')}
                value={draft.project}
                onChange={(project) => setDraft((d) => ({ ...d, project }))}
                useSearch={useProjects}
                getLabel={(p) => `${p.axisCode} - ${p.name}`}
              />
            </FormField>
          )}

          {isColumnVisible('client') && (
            <FormField label={t('filtros.cliente')}>
              <SearchCombobox
                placeholder={t('filtros.buscarPlaceholder')}
                value={draft.client}
                onChange={(client) => setDraft((d) => ({ ...d, client }))}
                useSearch={useClients}
                getLabel={(c) => c.name.trim()}
              />
            </FormField>
          )}

          {isColumnVisible('contract') && (
            <FormField
              label={t('filtros.contrato')}
              hint={draft.client ? t('filtros.contratoDoClienteHint') : undefined}
            >
              <SearchCombobox
                placeholder={t('filtros.buscarPlaceholder')}
                value={draft.contract}
                onChange={(contract) => setDraft((d) => ({ ...d, contract }))}
                useSearch={useContractsOfClient}
                getLabel={contractLabel}
              />
            </FormField>
          )}

          {isColumnVisible('allocatedPercentage') && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label={t('filtros.percentualMinimo')}>
                <NumberInput
                  value={draft.percentage.min}
                  onChange={(min) =>
                    setDraft((d) => ({ ...d, percentage: { ...d.percentage, min } }))
                  }
                  min={0}
                  max={100}
                  showControls={false}
                />
              </FormField>
              <FormField label={t('filtros.percentualMaximo')}>
                <NumberInput
                  value={draft.percentage.max}
                  onChange={(max) =>
                    setDraft((d) => ({ ...d, percentage: { ...d.percentage, max } }))
                  }
                  min={0}
                  max={100}
                  showControls={false}
                />
              </FormField>
            </div>
          )}

          {isColumnVisible('billable') && (
            <FormField label={t('tabela.faturando')}>
              <SegmentedControl
                aria-label={t('tabela.faturando')}
                value={toChoice(draft.billable)}
                onValueChange={(choice) =>
                  setDraft((d) => ({ ...d, billable: fromChoice(choice as BillableChoice) }))
                }
              >
                <SegmentedControl.Item value="all">
                  {t('consultas.filtros.todos')}
                </SegmentedControl.Item>
                <SegmentedControl.Item value="yes">{t('common:yes')}</SegmentedControl.Item>
                <SegmentedControl.Item value="no">{t('common:no')}</SegmentedControl.Item>
              </SegmentedControl>
            </FormField>
          )}

          {(isColumnVisible('startDate') || isColumnVisible('endDate')) && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label={t('filtros.periodoInicio')}>
                <DatePicker
                  value={draft.startDate}
                  onChange={(date) => setDraft((d) => ({ ...d, startDate: date as Date | null }))}
                  clearable
                />
              </FormField>
              <FormField label={t('filtros.periodoFim')} hint={t('filtros.periodoFimHint')}>
                <DatePicker
                  value={draft.endDate}
                  onChange={(date) => setDraft((d) => ({ ...d, endDate: date as Date | null }))}
                  clearable
                />
              </FormField>
            </div>
          )}

          {isColumnVisible('notes') && (
            <FormField label={t('filtros.anotacoes')}>
              <Input
                value={draft.notes}
                onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                placeholder={t('filtros.anotacoesPlaceholder')}
              />
            </FormField>
          )}

          {isColumnVisible('tags') && (
            <FormField label={t(ALLOCATION_COLUMN_LABEL_KEYS.tags)}>
              <MultiSelectFilter
                options={tagOptions.map((tag) => ({ id: tag.id, label: tag.name }))}
                value={draft.tags.map((tag) => tag.id)}
                onChange={(ids) =>
                  setDraft((d) => ({
                    ...d,
                    tags: tagOptions.filter((tag) => ids.includes(tag.id)),
                  }))
                }
              />
            </FormField>
          )}

          {MANAGER_FIELDS.map(
            ([column, key]) =>
              isColumnVisible(column) && (
                <FormField key={key} label={t(ALLOCATION_COLUMN_LABEL_KEYS[column])}>
                  <Input
                    value={draft[key]}
                    onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                    placeholder={t('filtros.nomeOuMatriculaPlaceholder')}
                  />
                </FormField>
              ),
          )}
        </form>
      </Drawer.Body>
      <Drawer.Footer className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          {t('filtros.cancelar')}
        </Button>
        <Button type="submit" form="allocations-advanced-filters">
          {t('filtros.aplicar')}
        </Button>
      </Drawer.Footer>
    </Drawer>
  );
}
